// discoverySearch.js — U2-INS (TEST 3.02, TEST 3.07): WHEN the app asks orella-api to search for
// a patient's insurance, WITH WHICH details, and WHAT the answer means. Import-free, so
// scripts/check-discovery-triggers.mjs executes it.
//
// ★★★ THE SERVER CONTRACT THIS READS (orella-api rcm_service.discover_insurance, live since
//   v243.268.0, B7; PROD-418's key-only write-back live since v243.269.0, D1):
//   * Every search is PAID. The patient lane holds 3 per insurance (primary, secondary), counted
//     server-side; the count can never be reset from here.
//   * When the policyholder is not the patient, the search carries the POLICYHOLDER's first
//     name, last name and date of birth, read from the SAVED profile (insurance, or
//     insurance.secondary for target 'secondary'). Incomplete -> 'policyholder_details_missing',
//     nothing sent, nothing paid. So the app saves those fields BEFORE it asks.
//   * The answers that are not a search come back 200 { discovery: <stored or null>, error,
//     searchesLeft }: DISCOVERY_REFUSALS below (rcm_service.DISCOVERY_REFUSALS, held equal there
//     by tools/test_discovery_cap.py). Unchanged inputs pressed again answer 'same_inputs' for
//     free (U-5); Home's 90-day re-check runs only while 2 of the 3 remain ('recheck_budget', U-3).
//
// ★★ THE TRIGGERS (Alex, TEST 3.02): never on the policyholder tap. A search starts when the
//   policyholder's details are entered, when a card is added that did not give both a carrier
//   and a member ID (U-7), when "Me" is chosen (U-6), or when Search is pressed.
export const DISCOVERY_REFUSALS = Object.freeze(['policyholder_details_missing', 'cap_reached', 'cap_check_failed', 'recheck_budget', 'same_inputs'])
export const DISCOVERY_CAP = 3
// The pause after the last keystroke before a "details entered" search (TEST 3.02).
export const SEARCH_DEBOUNCE_MS = 1500

// A full date of birth: ISO YYYY-MM-DD (what DobPicker emits), a 4-digit year from 1900, a real day.
export function isFullDob(value) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(value || '').trim())
  if (!m) return false
  const y = Number(m[1]); const mo = Number(m[2]); const d = Number(m[3])
  if (y < 1900) return false
  const dt = new Date(Date.UTC(y, mo - 1, d))
  return dt.getUTCFullYear() === y && dt.getUTCMonth() === mo - 1 && dt.getUTCDate() === d
}

// True when the plan holder is someone else (Spouse, Parent, Child, Other): the server then
// searches with that person's details (U-1). '' and 'Self' are the patient.
export function needsPolicyholder(relationship) {
  const r = String(relationship || '').trim().toLowerCase()
  return r !== '' && r !== 'self'
}

export function policyholderComplete(policy) {
  const p = policy || {}
  return !!(String(p.subscriberFirstName || '').trim() && String(p.subscriberLastName || '').trim() && isFullDob(p.subscriberDOB))
}

// ★ PROD-474 (U-7, Alex 2026-09-30): coverage the patient already gave, a carrier AND a member ID
//   (from the card, a search result or typed), is CHECKED (the eligibility check), never searched
//   for. A search over it spends one of the 3 paid searches to find what is already on file.
export function hasCarrierAndMemberId(policy) {
  const p = policy || {}
  return !!(String(p.provider || '').trim() && String(p.memberId || '').trim())
}

// ★ Self-pay at Finish (Alex, 2026-10-02), patient 242.229.0: onboarding Finish runs the paid
//   insurance search only for a patient who did NOT choose self-pay and gave no carrier + member
//   ID (PROD-474). A self-pay patient was searched at Finish (a paid Stedi call, one of the 3).
//   The eligibility check at Finish is not decided here and is unchanged.
export function finishSearchesInsurance(policy) {
  const p = policy || {}
  return p.selfPay !== true && !hasCarrierAndMemberId(p)
}

// The four keys the server reads for the search; the save before a search carries exactly these.
export function policyholderFields(policy) {
  const p = policy || {}
  return {
    subscriberRelationship: p.subscriberRelationship || '',
    subscriberFirstName: String(p.subscriberFirstName || '').trim(),
    subscriberLastName: String(p.subscriberLastName || '').trim(),
    subscriberDOB: String(p.subscriberDOB || '').trim(),
  }
}

// ★ PROD-822 review REVIEW_PNEXT2 round 2 B-r2-1 (2026-10-10): a stored date of birth as ISO YYYY-MM-DD,
//   or '' when it cannot be read. The same three shapes orella-api accepts (rcm_service
//   _normalize_date_yyyymmdd at 0a07b67): 'YYYY-MM-DD' (first 10 characters), 'MM/DD/YYYY' (a 2-digit
//   year: 19xx from 30, else 20xx) and 'YYYYMMDD'. The md panel's discovery Apply stores the payer's
//   raw 'YYYYMMDD'; isFullDob (ISO only) then kept policyholderPending true and every onboarding check
//   silently skipped. A result that is not a real date is ''.
export function isoDob(value) {
  const s = String(value ?? '').trim()
  if (!s) return ''
  let y, m, d
  if (s.length >= 10 && s[4] === '-' && s[7] === '-') [y, m, d] = [s.slice(0, 4), s.slice(5, 7), s.slice(8, 10)]
  else if (s.includes('/')) {
    const parts = s.split('/')
    if (parts.length !== 3) return ''
    m = parts[0].trim().padStart(2, '0'); d = parts[1].trim().padStart(2, '0'); y = parts[2].trim()
    if (/^\d{2}$/.test(y)) y = (Number(y) >= 30 ? '19' : '20') + y
  } else if (/^\d{8}$/.test(s)) [y, m, d] = [s.slice(0, 4), s.slice(4, 6), s.slice(6, 8)]
  else return ''
  const iso = `${y}-${m}-${d}`
  return isFullDob(iso) ? iso : ''
}

// ★ PROD-822 (review REVIEW_PNEXT2 A-1, 2026-10-10): the policyholder as an insurance WRITE carries
//   it (the onboarding section save and the step's eligibility PUT). orella-api asks the payer with
//   the SAVED row (PUT /profile shallow-merges the section; _build_270_payload reads subscriber*), so
//   what the client keys a check by must reach the row first. Nothing before a relationship is
//   chosen, and never an EMPTY name or date of birth: a shallow merge of '' would blank the one the
//   server holds (measured on 52166fb6, review proof P2b: a resume, "Child" tapped, a Re-check
//   overwrote the saved name and DOB with ''). A non-empty value always goes, so a corrected one does.
export function policyholderForWire(policy) {
  const p = policy || {}
  const rel = String(p.subscriberRelationship || '').trim()
  if (!rel) return {}
  const out = { subscriberRelationship: rel }
  for (const k of ['subscriberFirstName', 'subscriberLastName', 'subscriberDOB']) {
    const v = String(p[k] || '').trim()
    if (v) out[k] = v
  }
  return out
}

// ★ PROD-822 (A-1 P1): true while the plan holder is someone else and their first name, last name
//   or full date of birth is still missing. An eligibility check then asks the payer about a
//   subscriber with no name or DOB, a paid question with no answer worth having: the step and the
//   section save wait for the details (the section save then checks once, with them).
export function policyholderPending(policy) {
  const p = policy || {}
  return needsPolicyholder(p.subscriberRelationship) && !policyholderComplete(p)
}

// The policyholder's typed details, normalised. It does NOT read the relationship: switching the
// policyholder button must never look like new details (TEST 3.02: never on the tap).
export function policyholderSignature(policy) {
  const p = policy || {}
  const n = (v) => String(v || '').trim().replace(/\s+/g, ' ').toLowerCase()
  return [n(p.subscriberFirstName), n(p.subscriberLastName), n(p.subscriberDOB)].join('|')
}

// The identification PUT that precedes a search (the server reads the saved profile).
// ★ U2-INS C1b (PROD-394): it carries the STATE. Stedi "strongly recommends" it, and the
//   identity step's own save is fire-and-forget, so a search could run without one. The server
//   normalises it (rcm_service._normalize_us_state: "ca", "California" -> "CA"; anything else is
//   dropped, never sent). It is sent only when non-empty after trim: PUT /profile shallow-merges
//   identification, so `state: ''` would overwrite a stored state with nothing.
export function identificationForSearch(identity) {
  const i = identity || {}
  const state = typeof i.state === 'string' ? i.state.trim() : ''
  return {
    firstName: i.firstName, lastName: i.lastName,
    dob: i.dob, sex: i.sex, zipCode: i.zipCode,
    email: i.email,
    ...(state ? { state } : {}),
  }
}

const ANSWERED = ['found', 'multiple', 'not_found']

// What a POST /users/<id>/insurance-discovery answer means for the screen.
//   kind: 'found'   candidates to pick from
//         'none'    searched, nothing found
//         'pending' Stedi is still working (Home's poll resumes it); no sentence
//         'missing' the policyholder's details are incomplete; nothing was paid
//         'cap'     all 3 searches for this insurance are used
//         'same'    these details were already searched; the stored answer, free
//         'failed'  the search did not go through (an error, a refused count, a lost race,
//                   Home's re-check budget, or no answer at all). Never "couldn't find".
export function discoveryOutcome(resp) {
  const r = resp && typeof resp === 'object' ? resp : {}
  const dr = r.discovery && typeof r.discovery === 'object' ? r.discovery : null
  const left = Number.isInteger(r.searchesLeft) ? r.searchesLeft
    : (dr && Number.isInteger(dr.searchesLeft) ? dr.searchesLeft : null)
  const cands = (dr && Array.isArray(dr.candidates)) ? dr.candidates : []
  const err = typeof r.error === 'string' ? r.error : null
  let kind
  if (err === 'policyholder_details_missing') kind = 'missing'
  else if (err === 'cap_reached') kind = 'cap'
  else if (err === 'same_inputs') kind = 'same'
  else if (err && DISCOVERY_REFUSALS.includes(err)) kind = 'failed'
  else if (!dr) kind = 'failed'
  else if ((dr.status === 'found' || dr.status === 'multiple') && cands.length > 0) kind = 'found'
  else if (ANSWERED.includes(dr.status) && !err) kind = 'none'
  else if (dr.status === 'pending' && !err) kind = 'pending'
  else kind = 'failed'
  return { kind, discovery: dr, searchesLeft: left }
}

// The sentence for an outcome, as an i18n key. Onboarding speaks in the first person, the
// profile screens do not (U2-INS section 3). null: no sentence (found, pending, and onboarding's
// own "I couldn't find" card for none).
export function outcomeMessageKey(kind, surface = 'profile') {
  if (surface === 'onboarding') {
    if (kind === 'missing') return 'onboarding.insurance.needPolicyholderDetails'
    if (kind === 'same') return 'onboarding.insurance.sameDetails'
    if (kind === 'failed') return 'onboarding.insurance.searchFailed'
    if (kind === 'cap') return 'insuranceSearch.capReached'
    return null
  }
  if (kind === 'same') return 'insuranceSearch.sameDetails'
  if (kind === 'cap') return 'insuranceSearch.capReached'
  if (kind === 'failed' || kind === 'missing') return 'onboarding.insurance.searchFailed'
  if (kind === 'none') return 'profileInsurance2.noMatchingPlan'
  return null
}
